import { memo, useEffect, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Segmented, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { useGetMetricsQuery } from '@/redux';
import { ColumnTypesEnum } from '@/core';
import { TotalsTypesEnum, getOptionsDistributionChart } from './helpers';
import { EmptyChart } from './EmptyChart';
import { DownloadChartBtn } from './DownloadChartBtn';
import { useGetMaxQuantityShownColumns } from './hooks/useGetMaxQuantityShownColumns';
import classes from './DistributionChart.module.scss';

type DistributionChartPropsType = {
  startDate?: string;
  endDate?: string;
  setCostTypesLegends: (legends: string[]) => void;
};

type useGetDistributionChartDataPropsType = DistributionChartPropsType & { totalType: string };

const useGetDistributionChartData = ({
  startDate,
  endDate,
  setCostTypesLegends,
  totalType,
}: useGetDistributionChartDataPropsType) => {
  const { id } = useParams();
  const isRequestSkipped = !startDate || !endDate;
  const maxShownColumns = useGetMaxQuantityShownColumns();

  const { data: metricsData } = useGetMetricsQuery(
    { id: id!, startDate, endDate, type: ColumnTypesEnum.CostType },
    { skip: isRequestSkipped },
  );

  const { options, isChartExpanded } =
    getOptionsDistributionChart(totalType, maxShownColumns, metricsData) ?? {};

  useEffect(() => {
    if (options?.legend?.data) setCostTypesLegends(options.legend.data as string[]);
  }, [options, setCostTypesLegends]);

  return { options, isChartExpanded };
};

const DistributionChart = (props: DistributionChartPropsType) => {
  const totalsSelectOptions = Object.values(TotalsTypesEnum);
  const chartRef = useRef<ReactECharts | null>(null);
  const [totalType, setTotalType] = useState<string>(TotalsTypesEnum.costs);
  const { options, isChartExpanded } = useGetDistributionChartData({ ...props, totalType });

  const handleChangeTotalType = (totalType: string | number) => {
    setTotalType(String(totalType));
  };

  return (
    <>
      {options?.series?.length ? (
        <div className={classes.distributionChartWrapper}>
          <div className={classes.distributionChartPanel}>
            <div className={classes.titleWrapper}>
              <Typography.Title level={3} className="mb-0">
                Cost Buckets Distribution
              </Typography.Title>
              <DownloadChartBtn chartRef={chartRef} fileName="CostTypes.pdf" />
            </div>
            <div className={classes.distributionChartControls}>
              <Segmented onChange={handleChangeTotalType} options={totalsSelectOptions} />
            </div>
          </div>
          <ReactECharts
            ref={chartRef}
            style={{ height: isChartExpanded ? 400 : 345 }}
            notMerge={true}
            option={options}
          />
        </div>
      ) : (
        <EmptyChart title="Distribution" />
      )}
    </>
  );
};

export default memo(DistributionChart);
